import React, { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useApp } from "../contexts/AppContext";
import api from "../lib/api";
import { toast } from "sonner";
import { CreditCard, CheckCircle2, Loader2, Banknote, Smartphone, Landmark, X } from "lucide-react";

export default function EnrollPayment() {
  const { id } = useParams();
  const { lang } = useApp();
  const navigate = useNavigate();
  const goHome = () => navigate("/");
  const pt = lang !== "en";
  const tr = (a, b) => (pt ? a : b);
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");        // Stripe return
  const paypalToken = searchParams.get("token");           // PayPal return (order id)

  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState("");                    // which provider is loading
  const [phase, setPhase] = useState(
    sessionId ? "verifying" : (paypalToken ? "capturing" : "options")
  );

  // method-specific state
  const [showTransfer, setShowTransfer] = useState(false);
  const [mbwayPhone, setMbwayPhone] = useState("");
  const [mbwayReq, setMbwayReq] = useState(null);          // request_id while pending
  const [mb, setMb] = useState(null);                      // {entity, reference, amount}
  const [pixCpf, setPixCpf] = useState("");
  const [pixPhone, setPixPhone] = useState("");
  const [pix, setPix] = useState(null);                    // {order_id, qr_code_value, payment_url, amount}
  const [pixCopied, setPixCopied] = useState(false);

  const loadInfo = useCallback(async () => {
    const { data } = await api.get(`/enrollment/${id}/payment-info?lang=${lang}`);
    setInfo(data);
    return data;
  }, [id, lang]);

  useEffect(() => { loadInfo().catch(() => setInfo(false)); }, [loadInfo]);

  // Stripe: poll on return
  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0, active = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (!active) return;
        if (data.payment_status === "paid") { setPhase("paid"); loadInfo(); return; }
        if (data.status === "expired") { setPhase("failed"); return; }
      } catch (_) {}
      attempts += 1;
      if (attempts < 12 && active) setTimeout(poll, 2500);
      else if (active) setPhase("failed");
    };
    poll();
    return () => { active = false; };
  }, [sessionId, loadInfo]);

  // PayPal: capture on return
  useEffect(() => {
    if (!paypalToken) return;
    let active = true;
    (async () => {
      try {
        const { data } = await api.post(`/payments/enrollment/${id}/paypal/capture/${paypalToken}`);
        if (!active) return;
        setPhase(data.paid ? "paid" : "failed");
        if (data.paid) loadInfo();
      } catch (_) { if (active) setPhase("failed"); }
    })();
    return () => { active = false; };
  }, [paypalToken, id, loadInfo]);

  // MB WAY status polling while pending
  useEffect(() => {
    if (!mbwayReq) return;
    let active = true, attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/ifthenpay/mbway/status/${mbwayReq}`);
        if (!active) return;
        if (data.paid) { setPhase("paid"); setMbwayReq(null); loadInfo(); return; }
      } catch (_) {}
      attempts += 1;
      if (attempts < 40 && active) setTimeout(poll, 4000); // ~2.5min window
    };
    setTimeout(poll, 4000);
    return () => { active = false; };
  }, [mbwayReq, loadInfo]);

  // PIX status polling while a code is pending
  useEffect(() => {
    if (!pix?.order_id) return;
    let active = true, attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/ifthenpay/pix/status/${pix.order_id}`);
        if (!active) return;
        if (data.paid) { setPhase("paid"); setPix(null); loadInfo(); return; }
      } catch (_) {}
      attempts += 1;
      if (attempts < 90 && active) setTimeout(poll, 5000); // ~7.5min window
    };
    setTimeout(poll, 5000);
    return () => { active = false; };
  }, [pix, loadInfo]);

  const amount = info ? info.amount : 0;
  const curCode = info?.currency || "eur";
  const curSym = info?.currency_symbol || "€";
  const amountLabel = curCode === "eur" ? `${(amount || 0).toFixed(2)} €` : `${curSym}${(amount || 0).toFixed(2)}`;
  const isPT = (info?.country || "").trim().toLowerCase() === "portugal";
  const isEur = curCode === "eur";
  const isBrl = curCode === "brl";

  const payStripe = async () => {
    setBusy("stripe");
    try {
      const { data } = await api.post(`/payments/enrollment/${id}/checkout`, { origin_url: window.location.origin, lang });
      window.location.href = data.url;
    } catch (e) { toast.error(e.response?.data?.detail || tr("Erro", "Error")); setBusy(""); }
  };
  const payPaypal = async () => {
    setBusy("paypal");
    try {
      const { data } = await api.post(`/payments/enrollment/${id}/paypal/create-order`, { origin_url: window.location.origin, lang });
      window.location.href = data.approve_url;
    } catch (e) { toast.error(e.response?.data?.detail || tr("Erro", "Error")); setBusy(""); }
  };
  const payMbway = async () => {
    if (!mbwayPhone.trim()) { toast.error(tr("Indica o teu nº de telemóvel", "Enter your phone number")); return; }
    setBusy("mbway");
    try {
      const { data } = await api.post(`/payments/enrollment/${id}/ifthenpay/mbway`, { mobile: mbwayPhone, lang });
      setMbwayReq(data.request_id);
      toast.success(tr("Confirma o pagamento na app MB WAY", "Confirm the payment in the MB WAY app"));
    } catch (e) { toast.error(e.response?.data?.detail || tr("Erro MB WAY", "MB WAY error")); }
    finally { setBusy(""); }
  };
  const payMultibanco = async () => {
    setBusy("multibanco");
    try {
      const { data } = await api.post(`/payments/enrollment/${id}/ifthenpay/multibanco?lang=${lang}`);
      setMb(data);
    } catch (e) { toast.error(e.response?.data?.detail || tr("Erro Multibanco", "Multibanco error")); }
    finally { setBusy(""); }
  };
  const payPix = async () => {
    if (!pixCpf.trim()) { toast.error(tr("Indica o CPF do pagador", "Enter the payer's CPF")); return; }
    setBusy("pix");
    try {
      const { data } = await api.post(`/payments/enrollment/${id}/ifthenpay/pix`, { cpf: pixCpf, mobile: pixPhone, lang });
      setPix(data);
      toast.success(tr("Código PIX gerado. Paga na tua app do banco.", "PIX code generated. Pay in your bank app."));
    } catch (e) { toast.error(e.response?.data?.detail || tr("Erro PIX", "PIX error")); }
    finally { setBusy(""); }
  };
  const copyPix = () => {
    if (!pix?.qr_code_value) return;
    navigator.clipboard?.writeText(pix.qr_code_value);
    setPixCopied(true);
    setTimeout(() => setPixCopied(false), 2000);
  };

  if (info === null) return <div className="min-h-[50vh] flex items-center justify-center"><Loader2 className="animate-spin text-[#FF6B6B]" size={32} /></div>;
  if (info === false) return <div className="text-center pt-20 font-fredoka text-2xl text-[#0B1D3A]" data-testid="enroll-payment-notfound">{tr("Inscrição não encontrada.", "Enrollment not found.")}</div>;

  const quizName = lang === "en" ? info.quiz_name_en : (info.quiz_name_br || info.quiz_name_pt);
  const instructions = info.payment_instructions || "";

  if (phase === "paid" || info.credentials_sent) {
    return (
      <div className="max-w-2xl mx-auto px-6 pt-16 pb-20 text-center" data-testid="enroll-payment-success">
        <CheckCircle2 size={72} className="text-[#2D9C5A] mx-auto mb-4" />
        <h1 className="font-fredoka text-4xl font-bold text-[#0B1D3A] mb-3">{tr("Pagamento confirmado!", "Payment confirmed!")}</h1>
        <p className="font-nunito text-lg text-[#0B1D3A]/70">{tr("Obrigado! As credenciais dos alunos foram enviadas para o teu email.", "Thank you! The students' credentials have been emailed to you.")}</p>
        <button onClick={goHome} data-testid="enroll-payment-success-home"
          className="mt-6 inline-flex items-center justify-center px-6 py-3 rounded-2xl bg-[#0B1D3A] text-white font-fredoka font-bold hover:opacity-90 transition-opacity">
          {tr("Voltar ao início", "Back to home")}
        </button>
      </div>
    );
  }
  if (phase === "verifying" || phase === "capturing") {
    return (
      <div className="max-w-2xl mx-auto px-6 pt-20 pb-20 text-center" data-testid="enroll-payment-verifying">
        <Loader2 className="animate-spin text-[#FF6B6B] mx-auto mb-4" size={40} />
        <h1 className="font-fredoka text-3xl font-bold text-[#0B1D3A]">{tr("A confirmar o pagamento...", "Confirming your payment...")}</h1>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-6 md:px-10 pt-10 pb-20 relative" data-testid="enroll-payment-page">
      <button onClick={goHome} data-testid="enroll-payment-close"
        aria-label={tr("Fechar e voltar ao início", "Close and go home")}
        title={tr("Fechar e voltar ao início", "Close and go home")}
        className="absolute top-2 right-4 md:right-8 z-10 w-10 h-10 inline-flex items-center justify-center rounded-full bg-white border border-[#0B1D3A]/10 text-[#0B1D3A]/60 shadow-sm hover:bg-red-50 hover:text-red-500 hover:border-red-200 transition-colors">
        <X size={20} />
      </button>
      <h1 className="font-fredoka text-4xl font-bold text-[#0B1D3A] mb-2 text-center">{tr("Pagamento da inscrição", "Enrollment payment")}</h1>
      <p className="text-center font-nunito text-[#0B1D3A]/60 mb-6">{quizName} · {info.class_name} · {info.num_students} {tr("alunos", "students")}</p>

      {phase === "failed" && (
        <div className="rounded-2xl bg-red-50 border border-red-200 px-4 py-3 text-center mb-5" data-testid="enroll-payment-failed">
          <span className="font-nunito font-semibold text-red-600">{tr("O pagamento não foi concluído. Tenta novamente.", "Payment was not completed. Please try again.")}</span>
        </div>
      )}

      <div className="rounded-3xl bg-white p-6 md:p-8 border border-[#0B1D3A]/5 shadow-[0_12px_40px_-10px_rgba(11,29,58,0.1)] space-y-5">
        <div className="rounded-2xl bg-[#FFF6E5] border border-[#FFD93D] px-4 py-4 text-center" data-testid="enroll-payment-amount">
          <span className="block text-sm text-[#0B1D3A]/60 font-nunito">{tr("Total a pagar", "Total to pay")}</span>
          <span className="font-fredoka font-bold text-3xl text-[#0B1D3A]">{amountLabel}</span>
        </div>

        {/* Stripe */}
        <button onClick={payStripe} disabled={!!busy || amount <= 0} data-testid="pay-stripe-btn"
          className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-2xl bg-[#635BFF] hover:bg-[#5147e6] text-white font-fredoka font-bold text-lg transition-colors disabled:opacity-60">
          {busy === "stripe" ? <Loader2 size={18} className="animate-spin" /> : <CreditCard size={18} />}
          {tr("Cartão · Apple Pay · Google Pay", "Card · Apple Pay · Google Pay")}
        </button>

        {/* PayPal */}
        <button onClick={payPaypal} disabled={!!busy || amount <= 0} data-testid="pay-paypal-btn"
          className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-2xl bg-[#FFC439] hover:bg-[#f0b62f] text-[#003087] font-fredoka font-bold text-lg transition-colors disabled:opacity-60">
          {busy === "paypal" ? <Loader2 size={18} className="animate-spin" /> : <span className="font-extrabold italic">PayPal</span>}
        </button>

        {/* MB WAY — Portugal + EUR only */}
        {isPT && isEur && (
        <div className="rounded-2xl border-2 border-[#0B1D3A]/10 p-4">
          <div className="flex items-center gap-2 mb-2 text-[#0B1D3A] font-fredoka font-bold"><Smartphone size={18} /> MB WAY</div>
          {mbwayReq ? (
            <div className="flex items-center gap-2 text-[#0B1D3A]/70 font-nunito text-sm" data-testid="mbway-pending">
              <Loader2 size={16} className="animate-spin" /> {tr("Abre a app MB WAY e confirma o pagamento...", "Open the MB WAY app and confirm the payment...")}
            </div>
          ) : (
            <div className="flex gap-2">
              <input value={mbwayPhone} onChange={(e) => setMbwayPhone(e.target.value)} placeholder="912345678" data-testid="mbway-phone-input"
                className="flex-1 rounded-xl border-2 border-[#0B1D3A]/15 px-3 py-2 font-nunito focus:border-[#FF6B6B] outline-none" />
              <button onClick={payMbway} disabled={!!busy} data-testid="pay-mbway-btn"
                className="px-4 py-2 rounded-xl bg-[#E30613] text-white font-fredoka font-bold disabled:opacity-60">
                {busy === "mbway" ? <Loader2 size={16} className="animate-spin" /> : tr("Pagar", "Pay")}
              </button>
            </div>
          )}
        </div>
        )}

        {/* Multibanco — Portugal + EUR only */}
        {isPT && isEur && (
        <div className="rounded-2xl border-2 border-[#0B1D3A]/10 p-4">
          <div className="flex items-center gap-2 mb-2 text-[#0B1D3A] font-fredoka font-bold"><Landmark size={18} /> Multibanco</div>
          {mb ? (
            <div className="font-nunito text-sm text-[#0B1D3A]/80 space-y-1" data-testid="multibanco-reference">
              <div>{tr("Entidade", "Entity")}: <strong>{mb.entity}</strong></div>
              <div>{tr("Referência", "Reference")}: <strong>{mb.reference}</strong></div>
              <div>{tr("Montante", "Amount")}: <strong>{mb.amount} €</strong></div>
              <p className="text-xs text-[#0B1D3A]/50 mt-2">{tr("Paga no homebanking ou Multibanco. As credenciais são enviadas após a confirmação.", "Pay via homebanking or ATM. Credentials are sent after confirmation.")}</p>
            </div>
          ) : (
            <button onClick={payMultibanco} disabled={!!busy} data-testid="pay-multibanco-btn"
              className="px-4 py-2 rounded-xl bg-[#0B1D3A] text-white font-fredoka font-bold disabled:opacity-60">
              {busy === "multibanco" ? <Loader2 size={16} className="animate-spin" /> : tr("Gerar referência", "Generate reference")}
            </button>
          )}
        </div>
        )}

        {/* PIX — Brazil (BRL) only */}
        {isBrl && (
        <div className="rounded-2xl border-2 border-[#0B1D3A]/10 p-4" data-testid="pix-block">
          <div className="flex items-center gap-2 mb-2 text-[#0B1D3A] font-fredoka font-bold"><Landmark size={18} /> PIX</div>
          {pix ? (
            <div className="space-y-3" data-testid="pix-code">
              <p className="font-nunito text-sm text-[#0B1D3A]/70">{tr("Copia o código PIX e cola na tua app do banco (PIX Copia e Cola). As credenciais são enviadas após a confirmação.", "Copy the PIX code and paste it in your bank app (PIX Copy & Paste). Credentials are sent after confirmation.")}</p>
              <textarea readOnly value={pix.qr_code_value} rows={3} data-testid="pix-copypaste"
                className="w-full rounded-xl border-2 border-[#0B1D3A]/15 px-3 py-2 font-mono text-xs bg-[#F8FAFC]" />
              <div className="flex items-center gap-2">
                <button onClick={copyPix} data-testid="pix-copy-btn"
                  className="px-4 py-2 rounded-xl bg-[#0B1D3A] text-white font-fredoka font-bold text-sm">
                  {pixCopied ? tr("Copiado!", "Copied!") : tr("Copiar código", "Copy code")}
                </button>
                {pix.payment_url && (
                  <a href={pix.payment_url} target="_blank" rel="noreferrer" data-testid="pix-open-link"
                    className="px-4 py-2 rounded-xl border-2 border-[#0B1D3A]/15 text-[#0B1D3A] font-fredoka font-bold text-sm">
                    {tr("Abrir página PIX", "Open PIX page")}
                  </a>
                )}
              </div>
              <div className="flex items-center gap-2 text-[#0B1D3A]/60 font-nunito text-xs">
                <Loader2 size={14} className="animate-spin" /> {tr("A aguardar o pagamento...", "Waiting for payment...")}
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <input value={pixCpf} onChange={(e) => setPixCpf(e.target.value)} placeholder={tr("CPF do pagador", "Payer's CPF")} data-testid="pix-cpf-input"
                className="w-full rounded-xl border-2 border-[#0B1D3A]/15 px-3 py-2 font-nunito focus:border-[#FF6B6B] outline-none" />
              <input value={pixPhone} onChange={(e) => setPixPhone(e.target.value)} placeholder={tr("Telemóvel (opcional)", "Phone (optional)")} data-testid="pix-phone-input"
                className="w-full rounded-xl border-2 border-[#0B1D3A]/15 px-3 py-2 font-nunito focus:border-[#FF6B6B] outline-none" />
              <button onClick={payPix} disabled={!!busy || amount <= 0} data-testid="pay-pix-btn"
                className="w-full px-4 py-2.5 rounded-xl bg-[#32BCAD] text-white font-fredoka font-bold disabled:opacity-60">
                {busy === "pix" ? <Loader2 size={16} className="animate-spin inline" /> : tr("Gerar código PIX", "Generate PIX code")}
              </button>
            </div>
          )}
        </div>
        )}

        {/* Manual transfer */}
        <div className="border-t border-[#0B1D3A]/10 pt-4">
          <button onClick={() => setShowTransfer((s) => !s)} data-testid="pay-transfer-btn"
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl border-2 border-[#0B1D3A]/15 text-[#0B1D3A] font-fredoka font-bold hover:bg-[#0B1D3A]/5 transition-colors">
            <Banknote size={18} /> {tr("Pagar por transferência", "Pay by bank transfer")}
          </button>
          {showTransfer && (
            <div className="mt-3 rounded-2xl bg-[#F8FAFC] border border-[#0B1D3A]/10 px-4 py-3" data-testid="enroll-transfer-instructions">
              {instructions ? <p className="font-nunito text-sm text-[#0B1D3A]/80 whitespace-pre-wrap">{instructions}</p>
                : <p className="font-nunito text-sm text-[#0B1D3A]/60">{tr("Contacta-nos para os dados de transferência.", "Contact us for transfer details.")}</p>}
              <p className="font-nunito text-xs text-[#0B1D3A]/50 mt-3">{tr("Indica o código da inscrição: ", "Include the enrollment code: ")}<strong>{info.code}</strong>. {tr("As credenciais são enviadas após confirmarmos o pagamento.", "Credentials are sent once we confirm the payment.")}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
